import React, { forwardRef, type CSSProperties, type ReactNode } from "react";
import { mapColorToken, mapSpacingToken } from "./tokens";

export interface FlexProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  UNSAFE_style?: CSSProperties;
  UNSAFE_className?: string;
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  justifyContent?: CSSProperties["justifyContent"];
  alignItems?: CSSProperties["alignItems"];
  alignContent?: CSSProperties["alignContent"];
  gap?: string | number;
  wrap?: boolean | CSSProperties["flexWrap"];
  flex?: string | number;
  flexGrow?: number;
  flexShrink?: number;
  flexBasis?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  minHeight?: string | number;
  maxHeight?: string | number;
  width?: string | number;
  height?: string | number;
  margin?: string;
  marginX?: string;
  marginY?: string;
  marginTop?: string;
  marginBottom?: string;
  marginStart?: string;
  marginEnd?: string;
  backgroundColor?: string;
}

/**
 * Flex row/column layout mirroring common `@adobe/react-spectrum` Flex props (v3-style API).
 */
const Flex = forwardRef<HTMLDivElement, FlexProps>((props, ref) => {
  const {
    children,
    UNSAFE_style,
    UNSAFE_className,
    direction = "row",
    justifyContent,
    alignItems,
    alignContent,
    gap,
    wrap = false,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    width,
    height,
    margin,
    marginX,
    marginY,
    marginTop,
    marginBottom,
    marginStart,
    marginEnd,
    backgroundColor,
    className,
    style,
    ...rest
  } = props;

  const gapVal =
    typeof gap === "string" && gap.startsWith("size-")
      ? mapSpacingToken(gap)
      : gap;

  let flexWrapValue: CSSProperties["flexWrap"];
  if (wrap === true) {
    flexWrapValue = "wrap";
  } else if (wrap === false) {
    flexWrapValue = "nowrap";
  } else {
    flexWrapValue = wrap as CSSProperties["flexWrap"];
  }

  const mar = margin ? (mapSpacingToken(margin) ?? margin) : undefined;
  const marX = marginX ? (mapSpacingToken(marginX) ?? marginX) : undefined;
  const marY = marginY ? (mapSpacingToken(marginY) ?? marginY) : undefined;

  const merged: CSSProperties = {
    display: "flex",
    flexDirection: direction,
    justifyContent,
    alignItems,
    alignContent,
    gap: gapVal as string | number | undefined,
    flexWrap: flexWrapValue,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    width,
    height,
    margin: mar,
    marginTop: marginTop
      ? (mapSpacingToken(marginTop) ?? marginTop)
      : undefined,
    marginBottom: marginBottom
      ? (mapSpacingToken(marginBottom) ?? marginBottom)
      : undefined,
    marginInlineStart: marginStart
      ? (mapSpacingToken(marginStart) ?? marginStart)
      : undefined,
    marginInlineEnd: marginEnd
      ? (mapSpacingToken(marginEnd) ?? marginEnd)
      : undefined,
    marginInline: marX,
    marginBlock: marY,
    backgroundColor: mapColorToken(backgroundColor) ?? backgroundColor,
    ...UNSAFE_style,
    ...style,
  };

  return (
    <div
      ref={ref}
      className={[UNSAFE_className, className].filter(Boolean).join(" ")}
      style={merged}
      {...rest}
    >
      {children}
    </div>
  );
});

export default Flex;
